<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          <h1 class="h4 mb-1">{{ pageTitle }}</h1>
          <small class="text-muted">
            Sélectionnez les produits à déplacer vers {{ targetLabelWithName }}.
          </small>
        </div>

        <div class="d-flex flex-wrap gap-2">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm btn-icon-label"
            @click="goBack"
          >
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour</span>
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="alert alert-danger py-2">
        {{ errorMessage }}
      </div>

      <div class="card border-0 shadow-sm mb-3">
        <div class="card-body py-3">
          <div class="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <div class="small text-muted">Cible</div>
              <div class="fw-semibold">{{ targetLabelWithName }}</div>
            </div>
          </div>

          <div v-if="selectedItems.length" class="selected-products mt-3">
            <span
              v-for="item in selectedItems"
              :key="item.id"
              class="selected-product-pill"
            >
              <span>{{ item.name }}</span>
              <small v-if="item.sku">{{ item.sku }}</small>
              <button
                type="button"
                class="selected-product-remove"
                title="Retirer de la sélection"
                aria-label="Retirer de la sélection"
                :disabled="isSaving"
                @click="removeSelection(item.id)"
              >
                <i class="fa fa-xmark"></i>
              </button>
            </span>
          </div>
        </div>
      </div>

      <BootstrapDataTable
        :response="productsResponse"
        :columns="tableColumns"
        :loading="isLoadingProducts"
        :selectable="canAssignProducts"
        v-model:selected-row-ids="selectedRowIds"
        :selection-disabled="isSaving"
        :sort-by="table.sortBy"
        :sort-dir="table.sortDir"
        :client-pagination="false"
        :per-page="table.perPage"
        :state-key="tableStateKey"
        searchable
        v-model:search-value="filters.search"
        :search-placeholder="searchPlaceholder"
        empty-text="Aucun produit à associer."
        @state-restored="handleTableStateRestored"
        @page-change="handlePageChange"
        @per-page-change="handlePerPageChange"
        @sort-change="handleSortChange"
        @search-change="handleSearchChange"
        @refresh="fetchProducts"
      >
        <template #bulk-actions="{ selectedCount: count, isOverLimit }">
          <AppLoadingButton
            type="button"
            class="btn btn-primary btn-sm btn-icon-label assignment-save-button"
            :class="{ 'assignment-save-ready': count > 0 }"
            :loading="isSaving"
            loading-label="Enregistrement..."
            :disabled="isOverLimit"
            @click="saveSelection"
          >
            <i class="fa fa-save"></i>
            <span class="btn-label ms-1">Affecter {{ count }}</span>
          </AppLoadingButton>
        </template>

        <template #cell-name="{ row }">
          <div class="fw-semibold">{{ row.name }}</div>
          <small class="text-muted">{{ row.sku || row.barcode || '-' }}</small>
        </template>

        <template #cell-stock_quantity="{ row }">
          <span class="badge" :class="Number(row.stock_quantity || 0) <= 0 ? 'bg-danger' : 'bg-success'">
            {{ row.stock_quantity ?? 0 }}
          </span>
        </template>

        <template #cell-sale_price="{ row }">
          {{ $format.currency(row.sale_price || 0) }}
        </template>

        <template #cell-current_group="{ row }">
          {{ currentGroupLabel(row) }}
        </template>
      </BootstrapDataTable>
    </template>
  </AdminLayout>
</template>

<script>
import { showAppToast } from '@/support/toastBus';
import {
  clearProductAssignmentSelection,
  loadProductAssignmentSelection,
  saveProductAssignmentSelection,
} from '@/support/productAssignmentSelection';
import { BULK_ACTION_LIMIT, uniqueBulkIds } from '@/support/bulkActions';
import { hasPermission } from '@/support/permissions';

export default {
  name: 'ProductAssignmentView',
  props: {
    targetType: {
      type: String,
      required: true,
      validator: (value) => ['rayon', 'category'].includes(value),
    },
  },
  data() {
    return {
      target: null,
      productsResponse: null,
      selectedItems: [],
      isLoadingTarget: false,
      isLoadingProducts: false,
      isSaving: false,
      errorMessage: '',
      filters: {
        search: '',
      },
      table: {
        page: 1,
        perPage: 20,
        sortBy: 'name',
        sortDir: 'asc',
      },
      tableColumns: [
        { key: 'name', label: 'Produit', sortable: true },
        { key: 'stock_quantity', label: 'Stock', sortable: true, class: 'text-center' },
        { key: 'sale_price', label: 'Prix vente', sortable: true, class: 'text-end' },
        { key: 'current_group', label: 'Affectation actuelle' },
      ],
    };
  },
  computed: {
    currentUser() {
      return this.$store?.getters?.['auth/currentUser'] || null;
    },
    canAssignProducts() {
      return hasPermission(this.currentUser, 'products.update');
    },
    targetId() {
      return this.$route.params.id;
    },
    targetLabel() {
      return this.targetType === 'rayon' ? 'le rayon' : 'la catégorie';
    },
    targetLabelWithName() {
      return this.target?.name ? `${this.targetLabel} "${this.target.name}"` : this.targetLabel;
    },
    pageTitle() {
      return this.targetType === 'rayon'
        ? 'Associer des produits au rayon'
        : 'Associer des produits à la catégorie';
    },
    searchPlaceholder() {
      return 'Rechercher nom, SKU ou code-barres...';
    },
    tableStateKey() {
      return `product-assignment.${this.targetType}.${this.targetId}`;
    },
    selectedCount() {
      return this.selectedItems.length;
    },
    selectedRowIds: {
      get() {
        return this.selectedItems.map((item) => item.id);
      },
      set(ids) {
        if (this.isSaving) return;

        const selectedIds = uniqueBulkIds(ids);
        const visibleProducts = Array.isArray(this.productsResponse?.data)
          ? this.productsResponse.data
          : [];
        const knownProducts = [...this.selectedItems, ...visibleProducts];

        this.selectedItems = selectedIds.map((id) => {
          const product = knownProducts.find((item) => String(item.id) === String(id));

          return {
            id: Number(id),
            name: product?.name || `Produit #${id}`,
            sku: product?.sku || null,
          };
        });
        this.persistSelection();
      },
    },
    targetShowRouteName() {
      return this.targetType === 'rayon' ? 'RayShow' : 'CategoryShow';
    },
    targetBaseEndpoint() {
      return this.targetType === 'rayon' ? 'rayons' : 'categories';
    },
  },
  mounted() {
    this.restoreSelection();
    this.fetchTarget();
    this.fetchProducts();
  },
  methods: {
    restoreSelection() {
      this.selectedItems = loadProductAssignmentSelection(this.targetType, this.targetId, this.$store);
    },

    persistSelection() {
      saveProductAssignmentSelection(this.targetType, this.targetId, this.selectedItems, this.$store);
    },

    async fetchTarget() {
      this.isLoadingTarget = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get(`/${this.targetBaseEndpoint}/${this.targetId}`);
        this.target = response.data?.data || null;
      } catch (error) {
        console.error('Erreur chargement cible', error);
        this.errorMessage = `Impossible de charger ${this.targetLabel}.`;
      } finally {
        this.isLoadingTarget = false;
      }
    },

    async fetchProducts() {
      this.isLoadingProducts = true;
      this.errorMessage = '';

      try {
        const response = await this.$api.get(
          `/${this.targetBaseEndpoint}/${this.targetId}/assignable-products`,
          {
            params: {
              page: this.table.page,
              per_page: this.table.perPage,
              search: this.filters.search || undefined,
              sort_by: this.table.sortBy,
              sort_dir: this.table.sortDir,
            },
          }
        );

        this.productsResponse = response.data;
      } catch (error) {
        console.error('Erreur chargement produits associables', error);
        this.errorMessage = 'Impossible de charger les produits à associer.';
      } finally {
        this.isLoadingProducts = false;
      }
    },

    handleTableStateRestored({ page, perPage }) {
      this.table.page = page;
      this.table.perPage = perPage;
    },

    handlePageChange(page) {
      this.table.page = page;
      this.fetchProducts();
    },

    handlePerPageChange(perPage) {
      this.table.page = 1;
      this.table.perPage = perPage;
      this.fetchProducts();
    },

    handleSortChange({ sortBy, sortDir }) {
      this.table.page = 1;
      this.table.sortBy = sortBy;
      this.table.sortDir = sortDir;
      this.fetchProducts();
    },

    handleSearchChange(search) {
      this.table.page = 1;
      this.filters.search = search;
      this.fetchProducts();
    },

    removeSelection(productId) {
      if (this.isSaving) return;

      const id = Number(productId);
      this.selectedItems = this.selectedItems.filter((item) => Number(item.id) !== id);
      this.persistSelection();
    },

    clearSelection() {
      if (this.isSaving) return;

      this.selectedItems = [];
      this.persistSelection();
    },

    currentGroupLabel(row) {
      return this.targetType === 'rayon'
        ? (row.rayon || 'Sans rayon')
        : (row.categorie || 'Sans catégorie');
    },

    async saveSelection() {
      if (this.isSaving || this.selectedCount === 0 || this.selectedCount > BULK_ACTION_LIMIT) return;

      const selectedItems = this.selectedItems.map((item) => ({ ...item }));
      const selectedCount = selectedItems.length;
      this.isSaving = true;
      this.errorMessage = '';

      try {
        const confirmed = await this.$confirmAction({
          header: 'Affecter les produits',
          message: `Affecter les ${selectedCount} produits sélectionnés ${this.targetType === 'rayon' ? 'au rayon' : 'à la catégorie'} "${this.target?.name || ''}" ?`,
          acceptLabel: `Affecter ${selectedCount}`,
          acceptClass: 'p-button-primary',
        });
        if (!confirmed) return;

        const response = await this.$api.post(
          `/${this.targetBaseEndpoint}/${this.targetId}/assign-products`,
          {
            product_ids: selectedItems.map((item) => item.id),
          },
          { skipSuccessToast: true }
        );

        clearProductAssignmentSelection(this.targetType, this.targetId, this.$store);
        this.selectedItems = [];

        const updatedCount = Number(response.data?.data?.updated_count || 0);
        showAppToast({
          type: 'success',
          message: `${updatedCount} produit(s) associé(s) avec succès.`,
        });

        this.$router.push({ name: this.targetShowRouteName, params: { id: this.targetId } });
      } catch (error) {
        console.error('Erreur association produits', error);
        const errors = error.response?.data?.errors;
        this.errorMessage = errors
          ? Object.values(errors).flat().join(' ')
          : 'Impossible d’associer les produits sélectionnés.';
      } finally {
        this.isSaving = false;
      }
    },

    goBack() {
      this.$router.push({ name: this.targetShowRouteName, params: { id: this.targetId } });
    },
  },
};
</script>

<style scoped>
.assignment-save-button {
  overflow: hidden;
  position: relative;
}

.assignment-save-button.assignment-save-ready:not(:disabled)::after {
  animation: assignment-save-shine 2s ease-in-out infinite;
  background: linear-gradient(120deg, transparent, rgba(255, 255, 255, 0.45), transparent);
  content: '';
  inset: 0 auto 0 -120%;
  position: absolute;
  width: 70%;
}

.selected-products {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
}

.selected-product-pill {
  align-items: center;
  background: #eef6ff;
  border: 1px solid #cfe6ff;
  border-radius: 999px;
  color: #174a7c;
  display: inline-flex;
  gap: 0.35rem;
  max-width: 100%;
  padding: 0.25rem 0.45rem 0.25rem 0.65rem;
}

.selected-product-pill small {
  color: #5f7f9f;
}

.selected-product-remove {
  align-items: center;
  background: transparent;
  border: 0;
  color: inherit;
  display: inline-flex;
  height: 1.25rem;
  justify-content: center;
  padding: 0;
  width: 1.25rem;
}

@keyframes assignment-save-shine {
  0% {
    left: -120%;
  }
  60%,
  100% {
    left: 140%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .assignment-save-button.assignment-save-ready:not(:disabled)::after {
    animation: none;
    content: none;
  }
}
</style>
